/**
 * Global company scope control for the app bar (multi-company WI-1).
 *
 * A single per-user "Viewing: All companies ▾ · Acme UK · Acme US" select that
 * filters collection surfaces and seeds the default company for new records.
 * Distinct from the on-record company (`DocumentCompanyField`) — never fuse the
 * two (see docs/platform/multi-company-design.md, Decision history).
 *
 * Self-gating — renders nothing unless ALL hold:
 *   - the tenant has ≥2 accessible companies (hide-when-single),
 *   - the current module participates in the global scope (ERP cluster today),
 *   - the current surface is a collection surface (list/dashboard/report/kanban)
 *     — hidden on detail/form pages. The scope value is preserved while hidden,
 *     so back-navigation restores the filtered list (navigation invariant).
 *
 * Backed by `useCompanyScope()` and sent to the backend via the
 * `X-Active-Company-Id` header — a UI filter hint, NOT a security boundary.
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { cn } from '../../../lib/utils';
import { CompanyIdentity, COMPANY_CONTROL_TRIGGER_CLASS } from './CompanyIdentity';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { useMyCompanies } from '@/shared/users/hooks/useCompanies';
import { useCompanyScope } from '../../contexts/CompanyScopeContext';
import { shouldShowCompanyScopeForPath } from '../../utils/companyScope';
import { useCompanyScopingMapStore } from '../../utils/companyScopingStore';

const ALL_SENTINEL = '__all__';

export interface CompanyScopeSwitcherProps {
  className?: string;
}

export function CompanyScopeSwitcher({ className }: CompanyScopeSwitcherProps) {
  const { t } = useTranslation(['user_management']);
  const { pathname } = useLocation();
  const { data, isLoading, isSuccess } = useMyCompanies();
  const { companyScopeId, setCompanyScope } = useCompanyScope();
  // Subscribe to the participation map so the control re-evaluates visibility
  // when a tenant admin toggles this module's company scoping (WI-4).
  useCompanyScopingMapStore();

  const companies = data?.data ?? [];
  const selected = companyScopeId
    ? companies.find((c) => c.id === companyScopeId)
    : null;

  // Self-heal a stale scope: if the persisted company is no longer in the user's
  // accessible set (permissions changed), fall back to "All".
  //
  // This MUST also fire when the set is EMPTY. The persisted scope is still sent
  // as X-Active-Company-Id on every request, and the backend's governed read
  // routes now fail closed on a company the caller cannot access — an empty
  // accessible set included (resolve_active_company_scope). A user whose grants
  // dropped to zero would otherwise 403 on every list and report page with no way
  // out but clearing localStorage by hand.
  //
  // Gated on isSuccess, not !isLoading: a FAILED fetch also leaves `companies`
  // empty, and silently discarding a valid scope on a transient network error
  // would be its own bug.
  useEffect(() => {
    if (isSuccess && companyScopeId && !selected) {
      setCompanyScope(null);
    }
  }, [isSuccess, companyScopeId, selected, setCompanyScope]);

  // Hide on single-company tenants, non-participating modules, and
  // single-record surfaces. The scope value persists regardless.
  if (companies.length <= 1) return null;
  if (!shouldShowCompanyScopeForPath(pathname)) return null;
  const valueLabel = selected
    ? selected.name
    : t('user_management:companies.allCompanies', 'All companies');

  return (
    <Select
      value={companyScopeId ?? ALL_SENTINEL}
      onValueChange={(v) => setCompanyScope(v === ALL_SENTINEL ? null : v)}
      disabled={isLoading}
    >
      {/*
        Icon + company name sit as direct children of the trigger so the
        trigger's `[&>span]:line-clamp-1` only ever lands on the SelectValue
        span (one-line ellipsis) — never on a flex wrapper, which it would
        coerce to `display:-webkit-box` and stack the icon onto its own row.
        `[&>span]:min-w-0` lets that span shrink so long names truncate within
        `max-w-[14rem]` instead of overflowing. "Viewing" label dropped — the
        building icon already reads as a scope control (aria-label covers a11y).
      */}
      <SelectTrigger
        data-testid="company-scope-switcher"
        className={cn(
          COMPANY_CONTROL_TRIGGER_CLASS,
          'hover:bg-accent focus:ring-1 [&>span]:min-w-0',
          className,
        )}
        aria-label={t('user_management:companies.scopeAria', 'Company scope')}
      >
        {/*
          The swatch is a DIRECT child, and the name stays inside SelectValue —
          see the note above. `CompanyIdentity` renders its own flex wrapper, so
          nesting the composite inside SelectValue would put the trigger's
          `line-clamp-1` on a flex box and stack the swatch onto its own row.
          Under "All companies" there is no one company, so it falls back to the
          neutral building glyph.
        */}
        <CompanyIdentity company={selected} iconOnly />
        <SelectValue placeholder={valueLabel}>{valueLabel}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL_SENTINEL}>
          {t('user_management:companies.allCompanies', 'All companies')}
        </SelectItem>
        {companies.map((c) => (
          <SelectItem key={c.id} value={c.id}>
            <CompanyIdentity company={c} size="sm" />
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
